<!-- Thread — every page with one friend, oldest at the top, newest at the
     bottom. Screens role. -->
<template>
  <IonPage>
    <AppHeader :title="title" back="/tabs/pager">
      <IonButtons slot="end">
        <IonButton :disabled="!isFriend" @click="compose()">Page</IonButton>
      </IonButtons>
    </AppHeader>

    <IonContent ref="content" :fullscreen="true" class="pm-scr-content">
      <div class="pm-scr-pad">
        <!-- Who this is: the name first, the number under it. Tapping it saves
             the number as someone (the bar's own title can't be tapped). -->
        <button
          v-if="friend && !friend.system"
          type="button"
          class="pm-scr-namebar"
          :aria-label="`${title}${secondary ? ', ' + secondary : ''}. Save as`"
          @click="promptName(number)"
        >
          <span class="pm-scr-namebar-main">{{ title }}</span>
          <span class="pm-scr-muted pm-scr-small">
            <template v-if="secondary">{{ secondary }} · </template><span class="pm-scr-namebar-action">{{ friend.name ? 'Rename' : 'Save as…' }}</span>
          </span>
        </button>

        <!-- Not (yet) a friend: say why and offer the way on. -->
        <section v-if="friend && friend.status === 'pending-in'" class="pm-scr-notice" aria-label="Friend request">
          <p><strong>{{ title }}</strong> wants to page you. Nothing they send reaches your pager until you accept.</p>
          <div v-if="friend.firstPage" class="pm-scr-bubble pm-scr-bubble--in" :class="glassClass">
            <PageText :body="friend.firstPage" />
          </div>
          <div class="pm-scr-button-row">
            <IonButton :disabled="accept.busy.value" @click="accept.run()">{{ accept.busy.value ? 'Accepting…' : 'Accept' }}</IonButton>
            <IonButton fill="outline" color="danger" :disabled="block.busy.value" @click="confirmBlock">Block</IonButton>
          </div>
          <p v-if="accept.error.value || block.error.value" class="field-error" role="alert">{{ accept.error.value || block.error.value }}</p>
        </section>

        <section v-else-if="friend && friend.status === 'pending-out'" class="pm-scr-notice" aria-label="Request sent">
          <p>Waiting for {{ title }} to accept your request. You can page them once they do.</p>
          <IonButton fill="outline" :disabled="cancel.busy.value" @click="cancel.run()">Cancel request</IonButton>
          <p v-if="cancel.error.value" class="field-error" role="alert">{{ cancel.error.value }}</p>
        </section>

        <section v-else-if="friend && friend.status === 'blocked'" class="pm-scr-notice" aria-label="Blocked">
          <p>You blocked {{ title }}. Their pages don't reach you, and they can't send you a request.</p>
          <IonButton fill="outline" :disabled="unblock.busy.value" @click="unblock.run()">{{ unblock.busy.value ? 'Unblocking…' : 'Unblock' }}</IonButton>
          <p v-if="unblock.error.value" class="field-error" role="alert">{{ unblock.error.value }}</p>
        </section>

        <section v-else-if="!friend" class="pm-scr-notice" aria-label="Not a friend">
          <p>{{ title }} isn't one of your friends, so you can't page each other.</p>
          <IonButton fill="outline" router-link="/tabs/friends">Go to Friends</IonButton>
        </section>

        <!-- History -->
        <ol v-if="thread.length" class="pm-scr-thread" aria-label="Pages">
          <li
            v-for="p in thread"
            :key="p.id"
            class="pm-scr-msg"
            :class="p.dir === 'out' ? 'pm-scr-msg--out' : 'pm-scr-msg--in'"
          >
            <div
              class="pm-scr-bubble"
              :class="p.dir === 'out' ? 'pm-scr-bubble--out' : ['pm-scr-bubble--in', glassClass]"
            >
              <span class="sr-only">{{ p.dir === 'out' ? 'You' : title }}:</span>
              <PageText :body="p.body" />
              <p v-if="hint(p.body)" class="pm-scr-hint">{{ hint(p.body) }}</p>
            </div>
            <div class="pm-scr-msg-meta">
              <time :datetime="iso(p.sentAt)">{{ when(p.sentAt) }}</time>
              <StatusMark v-if="p.dir === 'out'" :status="p.status" />
              <span v-if="p.locked" class="pm-scr-tag">LOCKED</span>
              <button
                type="button"
                class="pm-scr-more"
                :aria-label="`More for the page ${p.dir === 'out' ? 'you sent' : 'from ' + title} at ${when(p.sentAt)}`"
                @click="openMenu(p, $event)"
              >
                <IonIcon :icon="ellipsisHorizontal" aria-hidden="true" />
              </button>
            </div>
          </li>
        </ol>

        <div v-else-if="isFriend" class="pm-scr-empty">
          <p>No pages with {{ title }} yet.</p>
          <IonButton @click="compose()">Send a page</IonButton>
        </div>
      </div>

      <ActionRail v-if="isFriend">
        <form v-if="twoWay" class="pm-scr-reply" @submit.prevent="reply.run()">
          <label class="sr-only" :for="`${uid}-thread-reply`">Reply to {{ title }}</label>
          <input
            :id="`${uid}-thread-reply`"
            :value="replyText"
            class="pm-scr-input pm-scr-input--lcd"
            type="text"
            placeholder="REPLY"
            autocomplete="off"
            enterkeyhint="send"
            :aria-describedby="`${uid}-thread-count`"
            @input="onReplyInput"
          >
          <span :id="`${uid}-thread-count`" class="pm-scr-counter pm-scr-counter--inline" :class="{ 'is-warn': replyCount >= 150 }">
            {{ replyCount }}/{{ MAX_GRAPHEMES }}
          </span>
          <button type="submit" class="sr-only" tabindex="-1" aria-hidden="true" />
          <IonButton type="button" :disabled="!replyText.trim() || reply.busy.value" @click="reply.run()">SEND</IonButton>
        </form>
        <IonButton v-else expand="block" @click="compose()">Reply</IonButton>
        <p v-if="reply.error.value" class="field-error" role="alert">{{ reply.error.value }}</p>
      </ActionRail>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { userMessage } from '../components/user-message.js';
import { ref, computed, watch, nextTick, onMounted, useId } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  IonPage, IonContent, IonButtons, IonButton, IonIcon,
  actionSheetController, alertController, onIonViewDidEnter,
} from '@ionic/vue';
import { ellipsisHorizontal } from 'ionicons/icons';
import { AppHeader, ActionRail, useAction, toast, announce } from '../core/ui/index.js';
import {
  settings, currentDevice, drafts,
  threadWith, friendName, friendSecondary, friendByNumber, markRead, toggleLock, deletePage, reportPage,
  acceptFriend, blockFriend, unblockFriend, cancelRequest, sendPage,
} from '../store.js';
import { whenLong } from '../components/when.js';
import { countGraphemes, MAX_GRAPHEMES } from '../pm/text.js';
import { hintFor } from '../pm/codebook.js';
import { sharePageImage } from '../pm/share-image.js';
import PageText from '../components/PageText.vue';
import StatusMark from '../components/StatusMark.vue';
import { promptName } from '../components/name-prompt.js';

// Per-instance ids: Ionic keeps earlier pages in the DOM, so a fixed id
// would repeat and break label/for pairing.
const uid = `pm${useId()}`;

const props = defineProps({ number: { type: String, required: true } });
const router = useRouter();
const route = useRoute();
const content = ref(null);

const friend = computed(() => friendByNumber(props.number));
const title = computed(() => friendName(props.number));
const secondary = computed(() => friendSecondary(props.number));
const isFriend = computed(() => friend.value?.status === 'accepted');
const thread = computed(() => threadWith(props.number) ?? []);
const twoWay = computed(() => currentDevice.value?.family === 'twoway');

const glassClass = computed(() => {
  if (settings.highContrast) return 'pm-scr-glass--hc';
  return `pm-scr-glass--${currentDevice.value?.lcd || 'classic'}`;
});

function compose() { router.push(`/tabs/compose/${props.number}`); }

// ── time + hints ────────────────────────────────────────────────────────
const iso = (ts) => (ts ? new Date(ts).toISOString() : '');
const when = (ts) => (ts ? whenLong(ts) : '');
function hint(bodyText) {
  const meaning = hintFor(bodyText || '');
  if (!meaning) return '';
  const code = String(bodyText).trim().split(/\s+/)[0];
  return `${code} = ${meaning.toUpperCase()}`;
}

// ── read + scroll ───────────────────────────────────────────────────────
function markShownRead() {
  for (const p of thread.value) if (p.dir === 'in' && !p.read) markRead(p.id);
}
async function toBottom(duration = 0) {
  await nextTick();
  content.value?.$el?.scrollToBottom?.(duration);
}
onMounted(() => {
  markShownRead(); toBottom();
  // Opened from the pager list for someone known only by number: ask for a name once.
  if (route.query.name === '1' && friend.value && !friend.value.name) {
    router.replace({ path: route.path });
    setTimeout(() => promptName(props.number), 350);
  }
});
onIonViewDidEnter(() => { markShownRead(); toBottom(); });
watch(() => thread.value.length, (n, old) => {
  markShownRead();
  toBottom(old ? 200 : 0);
});

// ── friendship actions ──────────────────────────────────────────────────
function must(r, fallback) {
  if (r && r.ok === false) throw new Error(userMessage(r, fallback));
  return r;
}
const accept = useAction(async () => must(await acceptFriend(props.number), "Couldn't accept. Try again."), { weight: 'pop' });
const block = useAction(async () => must(await blockFriend(props.number), "Couldn't block. Try again."), { weight: 'pop' });
const unblock = useAction(async () => must(await unblockFriend(props.number), "Couldn't unblock. Try again."));
const cancel = useAction(async () => must(await cancelRequest(props.number), "Couldn't cancel. Try again."));

async function confirmBlock() {
  const a = await alertController.create({
    header: `Block ${title.value}?`,
    message: "They won't be able to page you or send another request. They aren't told.",
    buttons: [
      { text: 'Cancel', role: 'cancel' },
      { text: 'Block', role: 'destructive', handler: () => { block.run(); } },
    ],
  });
  await a.present();
}

// ── inline reply (two-way devices) ─────────────────────────────────────
const replyText = ref(drafts[props.number] || '');
const replyCount = computed(() => countGraphemes(replyText.value));
let draftTimer = 0;
function onReplyInput(e) {
  const next = e.target.value;
  if (countGraphemes(next) > MAX_GRAPHEMES) {
    e.target.value = replyText.value;
    announce(`Pages stop at ${MAX_GRAPHEMES} characters.`);
    return;
  }
  replyText.value = next;
  clearTimeout(draftTimer);
  draftTimer = setTimeout(() => { drafts[props.number] = replyText.value; }, 300);
}
const reply = useAction(async () => {
  const text = replyText.value.trim();
  if (!text) return;
  const r = await sendPage(props.number, text);
  if (!r?.ok) throw new Error(userMessage(r, "Couldn't send that page. Try again."));
  clearTimeout(draftTimer);
  replyText.value = '';
  drafts[props.number] = '';
  const queued = !!r.queued;
  toast(queued ? 'QUEUED — WILL SEND WHEN ONLINE' : 'SENT ✓');
}, { weight: 'pop' });

// ── the ⋯ menu ─────────────────────────────────────────────────────────
async function openMenu(page, event) {
  const trigger = event?.currentTarget;
  const buttons = [
    { text: 'Copy', data: 'copy' },
    { text: page.locked ? 'Unlock' : 'Lock', data: 'lock' },
    { text: 'Share as image', data: 'share' },
  ];
  if (page.dir === 'in' && !friend.value?.system) buttons.push({ text: `Save ${title.value} as…`, data: 'name' });
  buttons.push({ text: 'Delete', role: 'destructive', data: 'delete' });
  if (page.dir === 'in') buttons.push({ text: 'Report', role: 'destructive', data: 'report' });
  buttons.push({ text: 'Cancel', role: 'cancel' });

  const sheet = await actionSheetController.create({ header: 'Page', buttons });
  await sheet.present();
  const { data } = await sheet.onDidDismiss();
  const choice = data?.action ?? data;
  try {
    if (choice === 'copy') {
      await navigator.clipboard.writeText(page.body);
      toast('Copied');
    } else if (choice === 'lock') {
      await toggleLock(page.id);
      announce(page.locked ? 'Locked.' : 'Unlocked.');
    } else if (choice === 'share') {
      await sharePageImage(page, currentDevice.value);
    } else if (choice === 'name') {
      await promptName(page.peer);
    } else if (choice === 'delete') {
      await deletePage(page.id);
    } else if (choice === 'report') {
      await report(page);
    }
  } catch (e) {
    toast(e?.message || 'That did not work. Try again.');
  }
  try { trigger?.focus?.(); } catch { /* gone */ }
}

const REASONS = ['Spam', 'Harassment or bullying', 'Threats or violence', 'Something else'];
async function report(page) {
  const sheet = await actionSheetController.create({
    header: 'Why are you reporting this page?',
    buttons: [...REASONS.map((r) => ({ text: r, data: r })), { text: 'Cancel', role: 'cancel' }],
  });
  await sheet.present();
  const { data: reason } = await sheet.onDidDismiss();
  if (!reason || typeof reason !== 'string') return;

  const confirm = await alertController.create({
    header: `Report and block ${title.value}?`,
    message: "We'll get a copy of this page. Reporting also blocks them — they won't be able to page you, and they aren't told.",
    buttons: [
      { text: 'Cancel', role: 'cancel' },
      { text: 'Report and block', role: 'destructive' },
    ],
  });
  await confirm.present();
  const { role } = await confirm.onDidDismiss();
  if (role !== 'destructive') return;
  const r = await reportPage(page, reason);
  if (r && r.ok === false) throw new Error(userMessage(r, "Couldn't send the report. Try again."));
  toast(`Reported. ${title.value} is blocked.`);
}
</script>
